import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { GET_URL_NEW } from '@utils/api/endpoints.ts';
import { BaseError } from '@utils/types/shared/base.ts';
import { AxiosError } from 'axios';
import { useNewAppSearchParams } from '@utils/hooks/use-new-app-search-params.ts';

export interface CustomModels {
  count: number;
  total_results: number;
  result: Model[];
}

export interface Model {
  id: number;
  key: string;
  name: string;
  last_modified: string;
  organization_id: number;
  language_data: null;
  is_trainable: boolean;
  type: number;
  base_language_id: number;
  base_language_key: string;
  training_log?: {
    language_id: number;
    training_status: number;
    log_name: null;
  };
}

export const useGetCustomModels = () => {
  const { searchParams } = useNewAppSearchParams();

  const offset = Number.isFinite(Number(searchParams.offset))
    ? Number(searchParams.offset)
    : 0;
  const limit = Number.isFinite(Number(searchParams.limit))
    ? Number(searchParams.limit)
    : 25;

  const sort = (searchParams.sort as string | undefined) || undefined;
  const direction =
    (searchParams.direction as 'asc' | 'desc' | undefined) || undefined;
  const q = (searchParams.q as string | undefined) || undefined;

  const fetchCustomModels = async () => {
    const { customModels } = GET_URL_NEW({
      offset,
      limit,
      q,
      sort,
      direction
    });
    const { data } = await Axios.get<CustomModels>(customModels);
    return data;
  };

  return useQuery<CustomModels, AxiosError<BaseError>>(
    ['customModels', offset, limit, q, sort, direction],
    fetchCustomModels,
    {
      // Only poll while at least one model is actively training (READY_TO_RUN=2
      // or RUNNING=3). Idle models don't change on their own, and the periodic
      // refetch was making the whole grid flicker every 20s for no reason.
      refetchInterval: data => {
        const hasActive = data?.result?.some(m => {
          const s = m.training_log?.training_status;
          return s === 2 || s === 3;
        });
        return hasActive ? 20000 : false;
      },
      refetchIntervalInBackground: true,
      // Idle pages should stay idle — the training-status gate above already
      // polls while something is running, and mutations invalidate this key
      // when the user makes changes. Refetching on stray focus events
      // (DevTools opening, brief tab-switch) was the "refreshes by itself"
      // the user reported.
      refetchOnWindowFocus: false,
      staleTime: 10000
    }
  );
};
